// Vendors
import React, { useState, useEffect } from 'react';
import qs from 'querystring';
import axios from 'axios';
import $ from 'jquery';

// Components
import {
  Appliances,
  DocumentEpat,
  DocumentGas,
  ProjectForm,
  Messages
} from './components';
import { Loader, DeleteProject } from '../components';

// Helpers
import { isCompletionDateCorrect } from '../components/helpers';

const projectDataDefaults = {
  construction_type: 'New',
  address: '',
  city: '',
  zipcode: '',
  completion_date: '',
  status: 'In Progress',
  document_gas: '0',
  document_epat: '0',
  epat_emissions: '',
  epat_emissions_baseline: '',
  epat_emissions_baseline_so2: '',
  epat_emissions_baseline_nox: '',
  epat_emissions_baseline_co2: '',
  epat_emissions_baseline_ch4: '',
  epat_emissions_baseline_n2o: '',
  epat_emissions_baseline_co2e: '',
  epat_emissions_alternative: '',
  epat_emissions_alternative_so2: '',
  epat_emissions_alternative_nox: '',
  epat_emissions_alternative_co2: '',
  epat_emissions_alternative_ch4: '',
  epat_emissions_alternative_n2o: '',
  epat_emissions_alternative_co2e: '',
  appliance_boiler: '',
  appliance_furnace: '',
  appliance_hydronic_air_handler: '',
  appliance_combi_boiler: '',
  appliance_water_heater: '',
  appliance_hybrid_heat_pump: '',
  appliance_cooktop_range: '',
  appliance_cooktop_range_change_from: '',
  appliance_oven: '',
  appliance_oven_change_from: '',
  appliance_grill: '',
  appliance_clothes_dryer: '',
  appliance_clothes_dryer_change_from: '',
  appliance_standby_generator: '',
  appliance_standby_generator_change_from: '',
  appliance_fireplace_gas_log: '',
  appliance_fireplace_gas_log_change_from: '',
  appliance_piping_propane_gas: '',
  appliance_piping_propane_gas_change_from: '',
  appliance_total_incentives: '',
  pid: null,
  user_builder_w9: '', // deprecated
  has_uploaded_w9: '',
  gas_rep_company_name: '',
  gas_rep_representative: '',
  gas_rep_city: '',
  gas_rep_state: ''
};

// Removes the iframe source on modal close, so the video stops playing.
if ($('#epat-modal__close').length) {
  $('#epat-modal__close').on('click', () => {
    const iframe = document.getElementById('epat-modal__video');
    const src = iframe.src;
    iframe.src = '';
    setTimeout(() => {
      iframe.src = src;
    }, 500);
  });
}

const ViewProject = () => {
  const [loading, setLoading] = useState(true);
  const [projectData, setProjectData] = useState(projectDataDefaults);
  const [view, setView] = useState('project-form');
  const [mainNotice, setMainNotice] = useState({ type: '', message: '' });
  const [showMainNotice, setShowMainNotice] = useState(false);
  const [requiredFieldsOK, setRequiredFieldsOK] = useState(true);
  const [isNewProject, setIsNewProject] = useState(true);
  const [readyForSubmittal, setReadyForSubmittal] = useState(0);
  const [unreadMessages, setUnreadMessages] = useState(
    projectData.new_messages
  );
  const [completionDateWrong, setCompletionDateWrong] = useState(false);
  const [projectDeleted, setProjectDeleted] = useState(false);
  const introText = window.wlPipFormMeta.intro;
  const canCreateNewProject = window.hasOwnProperty('wlPipProjectError')
    ? !!window.wlPipProjectError.canCreateNewProject
    : false;

  const getPageTitle = () => {
    const title = projectData.address;

    if (isNewProject) {
      return title === '' ? 'Add a Project' : title;
    }

    switch (view) {
      case 'document-epat':
        return `${title}: EPAT`;
      case 'document-gas':
        return `${title}: Gas Check`;
      case 'appliances':
        return `${title}: Appliances`;
      case 'messages':
        return `${title}: Messages`;
      case 'project-form':
      default:
        return title;
    }
  };

  const getIntroText = () => {
    switch (view) {
      case 'document-epat':
        return introText['epat'];
      case 'document-gas':
        return introText['gas'];
      case 'appliances':
        return introText['appliances'];
      case 'project-form':
      default:
        return introText['basic'];
    }
  };

  const displayCurrentView = () => {
    switch (view) {
      case 'appliances':
        return (
          <Appliances
            projectData={projectData}
            handleInputChange={handleInputChange}
            handleChangeView={handleChangeView}
            handleSaveProject={handleSaveProject}
            updateSpecificProjectDataProperty={
              updateSpecificProjectDataProperty
            }
          />
        );

      case 'document-epat':
        return (
          <DocumentEpat
            projectData={projectData}
            handleInputChange={handleInputChange}
            handleChangeView={handleChangeView}
            handleSaveProject={handleSaveProject}
            updateSpecificProjectDataProperty={
              updateSpecificProjectDataProperty
            }
          />
        );

      case 'document-gas':
        return (
          <DocumentGas
            projectData={projectData}
            handleChangeView={handleChangeView}
            handleSaveProject={handleSaveProject}
            updateSpecificProjectDataProperty={
              updateSpecificProjectDataProperty
            }
          />
        );

      case 'messages':
        return (
          <Messages
            projectData={projectData}
            setUnreadMessages={setUnreadMessages}
          />
        );

      case 'project-form':
      default:
        return (
          <ProjectForm
            projectData={projectData}
            unreadMessages={unreadMessages}
            requiredFieldsOK={requiredFieldsOK}
            epatFieldsOK={epatFieldsOK}
            isNewProject={isNewProject}
            handleInputChange={handleInputChange}
            handleChangeView={handleChangeView}
            completionDateWrong={completionDateWrong}
          />
        );
    }
  };

  const checkRequiredFields = () => {
    let {
      construction_type,
      address,
      city,
      state,
      zipcode,
      completion_date
    } = projectData;

    if (
      !construction_type ||
      !address ||
      !city ||
      !state ||
      zipcode.replace(/_/gi, '').length !== 5 ||
      completion_date.replace(/_/gi, '').replace(/\//gi, '').length !== 8
    ) {
      setRequiredFieldsOK(false);
      return false;
    }

    setRequiredFieldsOK(true);
    return true;
  };

  const epatFieldsOK = () => {
    const epatTableFields = ['so2', 'nox', 'co2', 'ch4', 'n2o', 'co2e'];
    let errors = 0;

    epatTableFields.map((field) =>
      Array.from(['alternative', 'baseline']).map((property) => {
        const propertyName = `epat_emissions_${property}_${field}`;
        if (
          projectData[propertyName] === '' ||
          projectData[propertyName] == '0'
        )
          errors++;
      })
    );

    if (projectData.document_epat === '' || projectData.document_epat == '0')
      errors++;

    return errors === 0;
  };

  const handleInputChange = (event) => {
    let newProjectData = projectData;

    // CompletionDate specific validation.
    if (event.currentTarget.name === 'completion_date') {
      setCompletionDateWrong(!isCompletionDateCorrect(event));
    }

    newProjectData[event.currentTarget.name] = event.currentTarget.value;
    setProjectData({ ...newProjectData });

    checkRequiredFields();
  };

  const handleChangeView = (view) => {
    setLoading(true);
    setShowMainNotice(false);

    setTimeout(() => {
      setLoading(false);
      view ? setView(view) : setView('project-form');
    }, Math.random() * 250);
  };

  const handleSaveProject = () => {
    setLoading(true);

    if (!checkRequiredFields()) {
      setMainNotice({
        type: 'error',
        message: 'Required fields can not be empty.'
      });
      setShowMainNotice(true);

      // Switch to 'project-form' view, since these required fields live there..
      if (view !== 'project-form') {
        setView('project-form');
      }

      setTimeout(() => setLoading(false), 250);
      return;
    }

    axios
      .post(
        window.wlPipConfig.ajaxUrl,
        qs.stringify({ action: 'wl_pip_save_project', ...projectData })
      )
      .then(({ data: { success, data } }) => {
        if (isNewProject && data.redirect) {
          window.location.href = data.redirect;
          return;
        }

        setMainNotice({
          type: success ? 'success' : 'error',
          message: data.message
        });
        setShowMainNotice(true);

        if (data.meta_fields) {
          setProjectData(data.meta_fields);
        }

        setTimeout(() => setLoading(false), 250);
      })
      .catch(({ data: { success, data } }) => {
        setMainNotice({
          type: success ? 'success' : 'error',
          message: data.message
        });
        setShowMainNotice(true);

        setTimeout(() => setLoading(false), 250);
      });
  };

  const handleSubmitProject = () => {
    setLoading(true);

    if (!readyForSubmittal) {
      setMainNotice({
        type: 'error',
        message:
          'Project is not ready for submittal yet. Please fill out to missing fields and uploads and missing documents.'
      });
      setShowMainNotice(true);

      // Switch to 'project-form' view, since these required fields live there..
      if (view !== 'project-form') {
        setView('project-form');
      }

      setLoading(false);
      return;
    }

    axios
      .post(
        window.wlPipConfig.ajaxUrl,
        qs.stringify({ action: 'wl_pip_submit_project', pid: projectData.pid })
      )
      .then(({ data: { success, data } }) => {
        setMainNotice({
          type: success ? 'success' : 'error',
          message: data.message
        });
        setShowMainNotice(true);
        updateSpecificProjectDataProperty('status', 'Submitted');

        setTimeout(() => setLoading(false), 250);
      })
      .catch((error) => {
        setMainNotice({
          type: 'error',
          message: error
        });
        setShowMainNotice(true);

        setTimeout(() => setLoading(false), 250);
      });
  };

  const handleDeleteProject = () => {
    setLoading(true);
    axios
      .post(
        window.wlPipConfig.ajaxUrl,
        qs.stringify({ action: 'wl_pip_delete_project', pid: projectData.pid })
      )
      .then(({ data: { success, data } }) => {
        setProjectDeleted(true);
        setLoading(false);
        setMainNotice({
          type: success ? 'success' : 'error',
          message: data.message
        });
        setShowMainNotice(true);

        setTimeout(() => location.reload(), 1000);
      })
      .catch((error) => {
        setMainNotice({
          type: 'error',
          message: error
        });
        setShowMainNotice(true);

        setTimeout(() => setLoading(false), 250);
      });
  };

  const updateSpecificProjectDataProperty = (key, value) => {
    let newProjectData = projectData;

    if (newProjectData.hasOwnProperty(key)) {
      newProjectData[key] = value;
      setProjectData({ ...newProjectData });
    }
  };

  useEffect(() => {
    // Determine whether we're editing a post, or creating a new one.
    if (window.wlPipProject === undefined) return setLoading(false);

    setProjectData(window.wlPipProject);
    setUnreadMessages(window.wlPipProject.new_messages);
    setIsNewProject(false);
    setLoading(false);
  }, []);

  useEffect(() => {
    if (projectData.firstView == 1 && view === 'project-form') {
      setMainNotice({
        type: 'success',
        message: (
          <>
            Project successfully created. Please continue your application below
            by completing the{' '}
            <span onClick={() => handleChangeView('appliances')}>
              <em>Appliances</em>
            </span>
            ,{' '}
            <span onClick={() => handleChangeView('document-gas')}>
              <em>Gas Check</em>
            </span>
            , and{' '}
            <span onClick={() => handleChangeView('document-epat')}>
              <em>EPAT</em>
            </span>{' '}
            sections.
          </>
        )
      });
      setShowMainNotice(true);
    }

    if (projectData.status === 'Inactive' && view === 'project-form') {
      setMainNotice({
        type: 'error',
        message: (
          <p>
            “This project was not submitted before the deadline and has been
            inactivated. Please contact us with any questions.”
          </p>
        )
      });
      setShowMainNotice(true);
    }
  }, [projectData]);

  // Check if project is ready for submittal
  useEffect(() => {
    // Basic project details
    const basicFields = requiredFieldsOK;

    // Appliances
    const appliances =
      projectData.appliance_total_incentives !== '' &&
      projectData.appliance_total_incentives != '0';

    // Gas document
    const doc_gas = () => {
      return (
        projectData.gas_rep_company_name &&
        projectData.gas_rep_representative &&
        projectData.gas_rep_city &&
        projectData.gas_rep_state &&
        projectData.document_gas !== '' &&
        projectData.document_gas != '0'
      );
    };

    // EPAT fields
    const epat = epatFieldsOK();

    // Builder W-9
    const doc_w9 =
      (projectData.user_builder_w9 !== '' &&
        projectData.user_builder_w9 != '0') ||
      (projectData.has_uploaded_w9 !== '' &&
        projectData.has_uploaded_w9 != '0');

    // Builder W-9
    const signedContract =
      projectData.signed_contract !== '' && projectData.signed_contract != '0';

    // Already submitted
    const alreadySubmitted = projectData.status !== 'Submitted';

    // Check all of the above..
    const projectIsReady =
      basicFields &&
      appliances &&
      doc_gas &&
      epat &&
      doc_w9 &&
      signedContract &&
      alreadySubmitted;

    // ..and set state.
    setReadyForSubmittal(projectIsReady);
  }, [projectData]);

  useEffect(() => {
    if (showMainNotice === false) return;

    setLoading(false);
    window.scrollTo(0, 0);
  }, [showMainNotice]);

  if (isNewProject && !canCreateNewProject) {
    return '';
  }

  if (loading === true || projectData === null) return <Loader />;

  return (
    <>
      {/* Back to {...} link */}
      {isNewProject ||
        (view === 'project-form' && (
          <h5
            className="edit-form__back"
            onClick={() => (window.location.href = '/pcip/dashboard/')}
          >
            Back to Dashboard
          </h5>
        ))}
      {view !== 'project-form' && (
        <h5
          className="edit-form__back"
          onClick={() => {
            handleChangeView();
          }}
        >
          Back to Project Overview
        </h5>
      )}

      {/* Show view's title */}
      <h1 className="u--margin-top-0 u--color-blue-dark">{getPageTitle()}</h1>

      {/* Show view's intro text, if available */}
      {getIntroText() !== '' && (
        <>
          <p dangerouslySetInnerHTML={{ __html: getIntroText() }} />
          {view === 'document-epat' && (
            <p>
              Need help? Watch{' '}
              <a
                href="#"
                onClick={(event) => {
                  event.preventDefault();
                  $('[data-modal="epat-modal"]').trigger('modal:show');
                }}
              >
                this video
              </a>{' '}
              to learn more about{' '}
              <a
                href="#"
                onClick={(event) => {
                  event.preventDefault();
                  $('[data-modal="epat-modal"]').trigger('modal:show');
                }}
              >
                How To Complete the EPAT report
              </a>{' '}
              below.
            </p>
          )}
          <hr />
        </>
      )}

      {/* Show notice message */}
      {showMainNotice === true && (
        <div
          className={`notice notice--${mainNotice.type}`}
          role="alert"
          style={{ maxWidth: '600px' }}
        >
          {mainNotice.message}
        </div>
      )}

      {!projectDeleted && (
        <>
          {/* Show whatever view we're on */}
          {displayCurrentView()}

          {/* Save/Submit btns */}
          {view === 'project-form' && (
            <>
              <hr />
              <button
                id="pip-add__save"
                className="btn btn--gold btn--lg module-hover-root u--margin-right-30-sm-min u--margin-bottom-20"
                type="button"
                disabled={
                  completionDateWrong === true ||
                  ['Submitted', 'Approved', 'Completed'].includes(
                    projectData.status
                  )
                }
                onClick={() => handleSaveProject()}
              >
                <span className="btn__content">
                  <span className="btn__text">
                    {isNewProject === true ? 'Create' : 'Save'} Project
                  </span>
                </span>
              </button>
              {isNewProject === false && (
                <button
                  className={`btn btn--lg module-hover-root ${
                    readyForSubmittal ? 'btn--gold' : 'btn--white'
                  } u--margin-right-30-sm-min u--margin-bottom-20`}
                  type="button"
                  disabled={
                    projectData.status === 'Inactive'
                      ? 'disabled'
                      : readyForSubmittal === false
                  }
                  onClick={() => handleSubmitProject()}
                >
                  <span className="btn__content">
                    <span className="btn__text">
                      {projectData.status === 'Submitted'
                        ? 'Project is under review'
                        : 'Submit for Review'}
                    </span>
                  </span>
                </button>
              )}
              {!isNewProject && (
                <DeleteProject handleDeleteProject={handleDeleteProject} />
              )}
            </>
          )}
        </>
      )}
    </>
  );
};

export default ViewProject;
